/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { ComponentChildren } from 'preact';
import { FunctionComponent, useLayoutEffect, useRef } from 'preact/compat';

interface PortalProps {
  children: ComponentChildren;
}

export const Portal: FunctionComponent<PortalProps> = ({ children }) => {
  const portalRoot = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    // Create portal root if it doesn't exist
    if (!portalRoot.current) {
      portalRoot.current = document.createElement('div');
      portalRoot.current.setAttribute('data-portal-root', '');
      document.body.appendChild(portalRoot.current);
    }

    // Move content to portal root
    if (contentRef.current && portalRoot.current) {
      portalRoot.current.appendChild(contentRef.current);
    }

    // Cleanup
    return () => {
      if (portalRoot.current) {
        portalRoot.current.remove();
        portalRoot.current = null;
      }
    };
  }, []);

  // Return a div that contains the children
  return (
    <div ref={contentRef} className="dropin-design">
      {children}
    </div>
  );
}; 